import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../../lib/api";
import { toast } from "sonner";

export default function Dashboard() {
  const [d, setD] = useState(null);
  const [rev, setRev] = useState(null);

  const load = () => Promise.all([
    api.get("/publisher/dashboard").then((r) => setD(r.data)),
    api.get("/tracking/summary?days=30").then((r) => setRev(r.data)).catch(() => setRev(null)),
  ]);
  useEffect(() => { load(); }, []);

  const runPublisher = async () => {
    try {
      await api.post("/publisher/run-now");
      toast.success("Auto-publish job ran.");
      load();
    } catch { toast.error("Job failed"); }
  };

  if (!d) return <div className="p-10 font-mono text-sm">Loading…</div>;
  const stats = [
    ["Articles", d.articles_total],
    ["In Review", d.drafts_in_review],
    ["Scheduled", d.scheduled],
    ["Published", d.published],
    ["Active Cards", d.cards_active],
    ["Active Deals", d.deals_active],
    ["Videos Queue", d.video_queue],
  ];
  return (
    <div className="p-8 md:p-10">
      <div className="flex items-center justify-between mb-8">
        <div>
          <div className="overline text-[#B4533C]">Dashboard</div>
          <h1 className="font-serif text-4xl mt-1">Editorial desk</h1>
        </div>
        <button onClick={runPublisher} className="bg-[#1B3B36] text-white px-5 py-2.5 text-sm tracking-wide hover:bg-[#112622]" data-testid="run-publisher-btn">
          Run auto-publish job now
        </button>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-7 grid-borders border-l border-t border-[#D6CFC4]" data-testid="dashboard-stats">
        {stats.map(([k, v]) => (
          <div key={k} className="p-5">
            <div className="overline text-[#4A4742]">{k}</div>
            <div className="font-serif text-3xl mt-1">{v}</div>
          </div>
        ))}
      </div>

      {rev && (
        <Link to="/admin/revenue" className="block mt-8 border border-[#D6CFC4] bg-[#EFECE5] hover:bg-[#E4DFD2] transition-colors p-6 grid grid-cols-2 md:grid-cols-4 gap-6" data-testid="dashboard-revenue-teaser">
          <div>
            <div className="overline text-[#B4533C]">Revenue · last 30d</div>
            <div className="font-serif text-4xl text-[#1B3B36] mt-2">${rev.total_commission_estimate.toFixed(2)}</div>
            <div className="text-xs font-mono text-[#4A4742] mt-1">estimated commission</div>
          </div>
          <div>
            <div className="overline text-[#4A4742]">Total clicks</div>
            <div className="font-serif text-3xl mt-1">{rev.total_clicks}</div>
          </div>
          <div>
            <div className="overline text-[#4A4742]">Booking clicks</div>
            <div className="font-serif text-3xl mt-1">{rev.by_type?.booking_click?.clicks || 0}</div>
          </div>
          <div>
            <div className="overline text-[#4A4742]">Card apply clicks</div>
            <div className="font-serif text-3xl mt-1">{rev.by_type?.card_click?.clicks || 0}</div>
          </div>
        </Link>
      )}

      <div className="mt-12 grid md:grid-cols-2 gap-10">
        <section>
          <h2 className="font-serif text-2xl mb-4">Expiring deals</h2>
          <div className="border border-[#D6CFC4]">
            <table className="w-full admin-table">
              <thead className="bg-[#EFECE5]"><tr><th className="text-left p-3">Title</th><th className="text-left p-3">Expires</th></tr></thead>
              <tbody>
                {d.expiring_deals.map((x) => (
                  <tr key={x.id} className="border-t border-[#D6CFC4]"><td className="p-3">{x.title}</td><td className="p-3 font-mono text-xs">{new Date(x.expires_at).toLocaleDateString()}</td></tr>
                ))}
                {d.expiring_deals.length === 0 && <tr><td colSpan={2} className="p-4 text-center text-[#4A4742] text-sm">No deals expiring soon.</td></tr>}
              </tbody>
            </table>
          </div>
        </section>
        <section>
          <h2 className="font-serif text-2xl mb-4">Expiring card offers</h2>
          <div className="border border-[#D6CFC4]">
            <table className="w-full admin-table">
              <thead className="bg-[#EFECE5]"><tr><th className="text-left p-3">Card</th><th className="text-left p-3">Ends</th></tr></thead>
              <tbody>
                {d.expiring_cards.map((x) => (
                  <tr key={x.id} className="border-t border-[#D6CFC4]"><td className="p-3">{x.card_name}</td><td className="p-3 font-mono text-xs">{new Date(x.end_date).toLocaleDateString()}</td></tr>
                ))}
                {d.expiring_cards.length === 0 && <tr><td colSpan={2} className="p-4 text-center text-[#4A4742] text-sm">No card offers expiring.</td></tr>}
              </tbody>
            </table>
          </div>
        </section>
      </div>

      <section className="mt-12">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-serif text-2xl">Recent publish activity</h2>
          <Link to="/admin/publish-log" className="overline text-[#B4533C]">View all →</Link>
        </div>
        <div className="border border-[#D6CFC4]">
          <table className="w-full admin-table">
            <thead className="bg-[#EFECE5]"><tr><th className="p-3 text-left">Time</th><th className="p-3 text-left">Article</th><th className="p-3 text-left">Actor</th><th className="p-3 text-left">Action</th><th className="p-3 text-left">Reason</th></tr></thead>
            <tbody>
              {d.recent_logs.map((l) => (
                <tr key={l.id} className="border-t border-[#D6CFC4]">
                  <td className="p-3 font-mono text-xs">{new Date(l.created_at).toLocaleString()}</td>
                  <td className="p-3 text-xs">{l.article_id}</td>
                  <td className="p-3 text-xs">{l.actor}</td>
                  <td className="p-3 text-xs">{l.action}</td>
                  <td className="p-3 text-xs">{l.reason || "—"}</td>
                </tr>
              ))}
              {d.recent_logs.length === 0 && <tr><td colSpan={5} className="p-4 text-center text-[#4A4742] text-sm">No activity yet.</td></tr>}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}
